var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Factory function to create a Firebase listener manager
 */
export function createFirebaseListenerManager(services) {
    var auth = services.auth, firestore = services.firestore, collection = services.collection, doc = services.doc, query = services.query, where = services.where, onSnapshot = services.onSnapshot, onAuthStateChanged = services.onAuthStateChanged;
    /**
     * Firebase Listener Manager - Centralized management of Firebase listeners
     * to prevent duplicate connections and memory leaks across MFEs
     */
    var FirebaseListenerManagerInstance = /** @class */ (function () {
        function FirebaseListenerManagerInstance() {
            this.firestoreListeners = new Map();
            this.authListeners = new Set();
            this.authUnsubscribe = null;
            console.log('Firebase Listener Manager initialized');
        }
        /**
         * Subscribe to auth state changes
         */
        FirebaseListenerManagerInstance.prototype.subscribeToAuthState = function (callback) {
            var _this = this;
            // Initialize auth listener if this is the first subscriber
            if (this.authListeners.size === 0 && !this.authUnsubscribe) {
                this.authUnsubscribe = onAuthStateChanged(auth, function (user) {
                    // Notify all subscribers
                    _this.authListeners.forEach(function (listener) { return listener.callback(user); });
                });
            }
            // Add this callback to the listeners
            var authListener = { callback: callback };
            this.authListeners.add(authListener);
            // Return unsubscribe function
            return function () {
                _this.authListeners.delete(authListener);
                // If no more listeners, remove the auth state subscription
                if (_this.authListeners.size === 0 && _this.authUnsubscribe) {
                    _this.authUnsubscribe();
                    _this.authUnsubscribe = null;
                }
            };
        };
        /**
         * Subscribe to a Firestore collection
         */
        FirebaseListenerManagerInstance.prototype.subscribeToCollection = function (path, callback, queryConstraints) {
            var _this = this;
            if (queryConstraints === void 0) { queryConstraints = []; }
            // Create a unique ID based on path and query constraints
            var constraintsStr = JSON.stringify(queryConstraints);
            var listenerId = "collection:".concat(path, ":").concat(constraintsStr);
            // Reuse existing listener if available
            if (!this.firestoreListeners.has(listenerId)) {
                console.log('no existing listeners for the collection adding one');
                // Create collection reference
                var colRef = collection(firestore, path);
                // Apply query constraints if any
                var ref = colRef;
                if (queryConstraints.length > 0) {
                    ref = query.apply(void 0, __spreadArray([colRef], queryConstraints, false));
                }
                // Create the snapshot listener
                var unsubscribe = onSnapshot(ref, function (snapshot) {
                    var data = snapshot.docs.map(function (doc) { return (__assign({ id: doc.id }, doc.data())); });
                    // Notify callbacks for this listener
                    var listener = _this.firestoreListeners.get(listenerId);
                    if (listener) {
                        // Store the latest data
                        listener.latestData = data;
                        listener.callbacks.forEach(function (cb) { return cb(data); });
                    }
                });
                // Store the listener
                this.firestoreListeners.set(listenerId, {
                    id: listenerId,
                    unsubscribe: unsubscribe,
                    callbacks: new Set([callback]),
                    latestData: [] // Initialize with empty array
                });
            }
            else {
                // Add callback to existing listener
                console.log('there are existing listeners for the collection so adding to existing set');
                var listener_1 = this.firestoreListeners.get(listenerId);
                if (listener_1) {
                    listener_1.callbacks.add(callback);
                    // Immediately invoke callback with latest data if available
                    if (listener_1.latestData) {
                        setTimeout(function () { var _a; return callback((_a = listener_1.latestData) !== null && _a !== void 0 ? _a : []); }, 0);
                    }
                }
            }
            // Return unsubscribe function
            return function () {
                var listener = _this.firestoreListeners.get(listenerId);
                if (listener) {
                    listener.callbacks.delete(callback);
                    console.log('deleted callback');
                    // If no more callbacks, remove the listener
                    if (listener.callbacks.size === 0) {
                        listener.unsubscribe();
                        _this.firestoreListeners.delete(listenerId);
                        console.log('unsybscribed from listening');
                    }
                }
            };
        };
        /**
         * Subscribe to a Firestore document
         */
        FirebaseListenerManagerInstance.prototype.subscribeToDocument = function (path, callback) {
            var _this = this;
            var listenerId = "document:".concat(path);
            if (!this.firestoreListeners.has(listenerId)) {
                // Create document reference
                var docRef = doc(firestore, path);
                // Create the snapshot listener
                var unsubscribe = onSnapshot(docRef, function (snapshot) {
                    var data = snapshot.exists() ? __assign({ id: snapshot.id }, snapshot.data()) : null;
                    // Notify callbacks
                    var listener = _this.firestoreListeners.get(listenerId);
                    if (listener) {
                        listener.callbacks.forEach(function (cb) { return cb(data); });
                    }
                });
                // Store the listener
                this.firestoreListeners.set(listenerId, {
                    id: listenerId,
                    unsubscribe: unsubscribe,
                    callbacks: new Set([callback])
                });
            }
            else {
                // Add callback to existing listener
                var listener = this.firestoreListeners.get(listenerId);
                if (listener) {
                    listener.callbacks.add(callback);
                }
            }
            // Return unsubscribe function
            return function () {
                var listener = _this.firestoreListeners.get(listenerId);
                if (listener) {
                    listener.callbacks.delete(callback);
                    if (listener.callbacks.size === 0) {
                        listener.unsubscribe();
                        _this.firestoreListeners.delete(listenerId);
                    }
                }
            };
        };
        /**
         * Subscribe to a nested collection or document path
         */
        FirebaseListenerManagerInstance.prototype.subscribeToPath = function (pathSegments, callback, isCollection) {
            if (isCollection === void 0) { isCollection = true; }
            var path = pathSegments.join('/');
            if (isCollection) {
                return this.subscribeToCollection(path, callback);
            }
            else {
                return this.subscribeToDocument(path, callback);
            }
        };
        /**
         * Get all active listeners for debugging
         */
        FirebaseListenerManagerInstance.prototype.getActiveListeners = function () {
            return {
                firestore: this.firestoreListeners.size,
                auth: this.authListeners.size
            };
        };
        /**
         * Clean up all listeners
         */
        FirebaseListenerManagerInstance.prototype.cleanup = function () {
            // Clean up Firestore listeners
            this.firestoreListeners.forEach(function (listener) {
                listener.unsubscribe();
            });
            this.firestoreListeners.clear();
            // Clean up Auth listener
            if (this.authUnsubscribe) {
                this.authUnsubscribe();
                this.authUnsubscribe = null;
            }
            this.authListeners.clear();
        };
        return FirebaseListenerManagerInstance;
    }());
    return new FirebaseListenerManagerInstance();
}
export default createFirebaseListenerManager;
